import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link } from "@tanstack/react-router"
import { motion } from "framer-motion"
import { ArrowRight, CheckCircle2, Globe, Send, Shield, Zap } from "lucide-react"

export const Route = createFileRoute("/features/payouts")({
  component: PayoutsPage,
})

const FEATURES = [
  {
    icon: Send,
    color: "bg-blue-light",
    iconColor: "text-blue",
    title: "Single API, any destination",
    desc: "Send funds to bank accounts, debit cards, or digital wallets in one unified call.",
    items: [
      "Bank account payouts",
      "Debit card push",
      "Digital wallet transfers",
      "Bulk batch payouts",
    ],
  },
  {
    icon: Globe,
    color: "bg-green-light",
    iconColor: "text-green-DEFAULT",
    title: "180+ countries supported",
    desc: "Pay recipients globally with local currency delivery and competitive FX rates.",
    items: [
      "180+ countries",
      "50+ currencies",
      "Real-time FX rates",
      "Local rails where available",
    ],
  },
  {
    icon: Zap,
    color: "bg-amber-light",
    iconColor: "text-amber-DEFAULT",
    title: "Instant & same-day",
    desc: "Deliver funds in seconds via instant payment rails or same-business-day settlement.",
    items: ["Instant push payouts", "Same-day ACH", "SEPA Instant Credit", "FPS (UK)"],
  },
  {
    icon: Shield,
    color: "bg-rose-light",
    iconColor: "text-rose-DEFAULT",
    title: "Compliance built-in",
    desc: "AML screening, sanctions checks, and recipient verification baked into every payout.",
    items: [
      "AML/KYC screening",
      "Sanctions list checks",
      "Recipient verification",
      "Full audit trail",
    ],
  },
]

const PAYOUT_FEED = [
  {
    name: "Sarah M.",
    dest: "Barclays ···7821",
    amount: "£1,240.00",
    status: "Sent",
    time: "0.3s",
    flag: "🇬🇧",
  },
  {
    name: "Carlos R.",
    dest: "BBVA ···4432",
    amount: "€890.00",
    status: "Sent",
    time: "0.4s",
    flag: "🇪🇸",
  },
  {
    name: "Aisha K.",
    dest: "Debit ···3391",
    amount: "$2,100.00",
    status: "Sent",
    time: "1.1s",
    flag: "🇺🇸",
  },
  { name: "Mei L.", dest: "Alipay", amount: "¥15,400", status: "Sent", time: "0.8s", flag: "🇨🇳" },
  {
    name: "James O.",
    dest: "GTBank ···9910",
    amount: "₦480,000",
    status: "Sent",
    time: "2.3s",
    flag: "🇳🇬",
  },
]

function PayoutsPage() {
  useSEO({
    title: "Payouts & Disbursements",
    description:
      "Send funds to anyone, anywhere. Fast, compliant payouts to bank accounts, cards, and wallets across 180+ countries.",
    canonical: "https://orchestrate.global/features/payouts",
  })
  return (
    <>
      <section className="bg-surface grid-bg relative overflow-hidden pt-32 pb-20">
        <div className="hero-glow" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div>
              <AnimatedSection>
                <SectionLabel label="Payouts & Disbursements" className="mb-5" />
              </AnimatedSection>
              <AnimatedSection delay={0.1}>
                <h1 className="font-display mb-6 text-5xl leading-tight tracking-tight sm:text-6xl lg:text-7xl">
                  Send money
                  <br />
                  <em className="text-blue not-italic">anywhere, instantly.</em>
                </h1>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <p className="text-muted mb-10 text-xl leading-relaxed">
                  Pay recipients globally with a single API call. Bank accounts, debit cards, and
                  digital wallets across 180 countries — with compliance handled automatically.
                </p>
              </AnimatedSection>
              <AnimatedSection delay={0.3}>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" size="lg" asChild>
                    <Link to="/contact">
                      Start sending payouts <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/docs">Read the docs</Link>
                  </Button>
                </div>
              </AnimatedSection>
            </div>
            <AnimatedSection direction="right" delay={0.2}>
              <div className="border-border rounded-2xl border bg-white p-6 shadow-xl">
                <div className="mb-5 flex items-center justify-between">
                  <span className="text-sm font-semibold">Live Payouts</span>
                  <span className="text-green-DEFAULT flex items-center gap-1.5 text-xs font-semibold">
                    <span className="bg-green-DEFAULT h-1.5 w-1.5 animate-pulse rounded-full" />
                    Processing
                  </span>
                </div>
                {PAYOUT_FEED.map((p, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                    className="border-border flex items-center justify-between border-b py-3 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{p.flag}</span>
                      <div>
                        <p className="text-sm font-medium">{p.name}</p>
                        <p className="text-muted text-xs">{p.dest}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{p.amount}</p>
                      <p className="text-green-DEFAULT text-xs font-medium">
                        {p.status} · {p.time}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-14 text-center">
              <SectionLabel label="Capabilities" centered className="mb-5" />
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">
                Built for any<em className="text-blue not-italic"> disbursement use case</em>
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.09, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                >
                  <div className="border-border h-full rounded-2xl border bg-white p-6 transition-all duration-250 hover:-translate-y-1.5 hover:shadow-xl">
                    <div
                      className={`h-11 w-11 ${f.color} mb-4 flex items-center justify-center rounded-xl`}
                    >
                      <Icon className={`h-5 w-5 ${f.iconColor}`} />
                    </div>
                    <h3 className="mb-2 text-base font-semibold">{f.title}</h3>
                    <p className="text-muted mb-4 text-xs leading-relaxed">{f.desc}</p>
                    <ul className="space-y-1.5">
                      {f.items.map((it) => (
                        <li key={it} className="text-muted flex items-center gap-2 text-xs">
                          <CheckCircle2 className="text-green-DEFAULT h-3.5 w-3.5 flex-shrink-0" />
                          {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <AnimatedSection>
            <SectionLabel label="Performance" centered className="mb-5" />
            <h2 className="font-display mb-14 text-4xl tracking-tight sm:text-5xl">
              Speed that moves<em className="text-blue not-italic"> business forward</em>
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-3 gap-6">
            {[
              { val: "0.4s", label: "Avg instant payout time", sub: "via supported rails" },
              { val: "180+", label: "Countries supported", sub: "with local rail coverage" },
              { val: "99.95%", label: "Payout success rate", sub: "across all destinations" },
            ].map((s, i) => (
              <AnimatedSection key={s.label} delay={i * 0.1}>
                <div className="border-border rounded-2xl border bg-white p-6">
                  <p className="font-display text-blue mb-2 text-3xl">{s.val}</p>
                  <p className="mb-1 text-sm font-semibold">{s.label}</p>
                  <p className="text-muted text-xs">{s.sub}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  )
}
